import { useState, type ReactElement } from 'react';
import type { TreeNode } from '../api';
import { NodeCard } from './NodeCard';
import { PeopleLine } from './PeopleLine';
import './NodeSeals.css';

/**
 * The lights under an Ember's title (v4.0 §4.17): one row per light this
 * telling stood at, each led by the light itself — the same amber lamp as on
 * the tree (2026-09-08 走查: the tilted amber square with `stood here` crammed
 * in two lines read as a badge in a stranger's typeface; the row already says
 * where it stands). Tap one for the light's paper — the place, how many stood
 * there, and the way to its page; no "I've been here too" (2026-09-06 走查:
 * the reader just did). Nothing while the pass is still running; nothing when
 * it stood nowhere.
 */
export function NodeSeals({ nodes, status, animate = false, emberId, conversationId }: {
  nodes: TreeNode[];
  status: 'pending' | 'done';
  /** Fall in one by one (a fresh Result); otherwise sit still (a revisit). */
  animate?: boolean;
  emberId?: string;
  conversationId?: string;
}): ReactElement | null {
  const [open, setOpen] = useState<{ node: TreeNode; anchor: { x: number; y: number } } | null>(null);
  if (status === 'pending' && nodes.length === 0) return null;
  if (nodes.length === 0) return null;
  return (
    <div className={`node-seals${animate ? ' is-arriving' : ''}`}>
      <p className="node-seals-lit">Where this stands on the tree</p>
      <ul className="node-seals-list">
        {nodes.map((node, i) => (
          <li key={node.id} className="node-seal" style={{ animationDelay: `${0.2 + i * 0.4}s` }}>
            <button
              type="button"
              className="node-seal-btn"
              data-log="result_seal_open"
              data-log-node-id={node.id}
              data-log-ember-id={emberId}
              data-log-conversation-id={conversationId}
              onClick={(e) => {
                const host = e.currentTarget.closest('.node-seals') as HTMLElement | null;
                const box = e.currentTarget.getBoundingClientRect();
                const hb = host?.getBoundingClientRect();
                setOpen((cur) => (cur?.node.id === node.id ? null : {
                  node,
                  anchor: { x: box.left + box.width - (hb?.left || 0), y: box.top + box.height / 2 - (hb?.top || 0) },
                }));
              }}
              aria-expanded={open?.node.id === node.id}
            >
              <span className="node-seal-light" aria-hidden />
              <span className="node-seal-label">
                {node.title}
                <small>{node.people_count > 1 ? <PeopleLine count={node.people_count} /> : 'You’re the first to stand here'}</small>
              </span>
              <svg className="node-seal-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </li>
        ))}
      </ul>
      {open ? (
        <NodeCard
          node={open.node}
          anchor={open.anchor}
          hostWidth={typeof window === 'undefined' ? 390 : Math.min(window.innerWidth, 760)}
          onClose={() => setOpen(null)}
          placeLink={open.node.status === 'public'}
          tellDoor={false}
          logDiscovery="result_seal"
        />
      ) : null}
    </div>
  );
}
